/**
 * Which time-of-day greeting the tenant dashboard opens with (COL-533).
 *
 * The design exports only show the morning frame ("Good morning, Luis."), and
 * the first implementation read that as a binary: before 11:00 → "Good
 * morning", otherwise the neutral "Hi {name}." So the dashboard said "Hi" for
 * two thirds of the day, including the evening, where every other surface of
 * this product greets in German convention (Guten Morgen / Guten Tag / Guten
 * Abend). A neutral fallback for 13 hours is not what the frame is an example
 * of — it is the morning case of a three-way split.
 *
 * Boundaries are the German ones, because that is the register the copy is
 * written in and the other five languages are translated from it:
 *
 *   00:00–10:59  Guten Morgen   Good morning
 *   11:00–17:59  Guten Tag      Good afternoon
 *   18:00–23:59  Guten Abend    Good evening
 *
 * Pure and hour-based so it can be tested at every boundary without faking a
 * clock in the component.
 */

export type TenantGreetingSlot = "morning" | "afternoon" | "evening";

export const AFTERNOON_FROM_HOUR = 11;
export const EVENING_FROM_HOUR = 18;

/**
 * `date` is passed in rather than read here, so the caller's own clock tick
 * (or a test's fixed date) decides. An out-of-range or invalid date falls back
 * to the afternoon greeting — the neutral one — rather than throwing on a
 * dashboard's headline.
 */
export function tenantGreetingSlot(
  date: Date = new Date()
): TenantGreetingSlot {
  const hour = date.getHours();
  if (!Number.isFinite(hour)) return "afternoon";
  if (hour < AFTERNOON_FROM_HOUR) return "morning";
  if (hour < EVENING_FROM_HOUR) return "afternoon";
  return "evening";
}

/**
 * Fill the `{name}` placeholder, and cope with not having a name.
 *
 * `profiles.name` can be empty — a tenant created from an invite before anyone
 * filled it in — and "Good morning, ." is worse than "Good morning." So the
 * dangling comma is collapsed rather than left in the headline.
 */
export function formatTenantGreeting(
  template: string,
  firstName: string
): string {
  return template.replace("{name}", firstName.trim()).replace(/,\s*\./, ".");
}
